import { useState } from 'react';
import { formatTl, tokens } from '../lib/api';

type Degisen = { code: string; name: string; eskiKurus: number | null; yeniKurus: number; artisYuzde: number | null };
type Onizleme = {
  kodSutun: string; fiyatSutun: string;
  degisen: Degisen[];
  bulunamayan: string[]; bulunamayanAdet: number;
  okunamayan: string[]; okunamayanAdet: number;
};

/**
 * FİYAT LİSTESİ İÇE AKTARIMI (şartname 4.2).
 *
 * ÖNCE ÖNİZLEME, sonra yazma. Yanlış sütun seçimi tüm ürünlerin liste fiyatını
 * bozar ve geri alması elle düzeltme gerektirir; kullanıcı neyin değişeceğini
 * görmeden "uygula" diyemiyor.
 */
export default function FiyatListesi({ kapat, bitti }: { kapat: () => void; bitti: () => void }) {
  const [dosya, setDosya] = useState<File | null>(null);
  const [onizleme, setOnizleme] = useState<Onizleme | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [sonuc, setSonuc] = useState<{ guncellenen: number } | null>(null);

  async function gonder(uygula: boolean) {
    if (!dosya) return;
    setHata(null); setBekliyor(true);
    try {
      const fd = new FormData();
      fd.append('dosya', dosya);
      const res = await fetch(`/api/urunler/fiyat-listesi${uygula ? '' : '?onizleme=1'}`, {
        method: 'POST',
        headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? 'Okunamadı');
      if (uygula) { setSonuc(j); setOnizleme(null); bitti(); }
      else setOnizleme(j);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Okunamadı');
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Fiyat listesi">
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Stok</div>
              <h2 className="ccd-heading">Fiyat listesi yükle</h2>
            </div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>

          <div className="ccd-banner">
            <span className="ccd-banner-icon" aria-hidden="true">i</span>
            <div className="ccd-meta">
              Tedarikçi Excel'i yükleyin. Kod ve fiyat sütunları başlıktan bulunur;
              <b> önce ne değişeceği gösterilir</b>, onaylamadan hiçbir fiyat yazılmaz.
              Tedarikçi kodları da (takma ad üzerinden) tanınır.
            </div>
          </div>

          <div className="ccd-field">
            <label className="ccd-field-label" htmlFor="fl">Excel dosyası</label>
            <input id="fl" className="ccd-input" type="file" accept=".xlsx,.xls"
                   onChange={(e) => { setDosya(e.target.files?.[0] ?? null); setOnizleme(null); setSonuc(null); }} />
          </div>

          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon">!</span><span>{hata}</span>
            </div>
          )}

          {sonuc && (
            <div className="ccd-banner">
              <span className="ccd-banner-icon" aria-hidden="true">✓</span>
              <span>{sonuc.guncellenen} ürünün liste fiyatı güncellendi.</span>
            </div>
          )}

          {!onizleme ? (
            <button className="ccd-btn ccd-btn-primary" disabled={!dosya || bekliyor}
                    onClick={() => gonder(false)}>
              {bekliyor ? 'Okunuyor…' : 'Önizle'}
            </button>
          ) : (
            <>
              <div className="ccd-meta">
                Sütunlar: kod = <b>{onizleme.kodSutun}</b>, fiyat = <b>{onizleme.fiyatSutun}</b>
              </div>

              {onizleme.degisen.length === 0 ? (
                <div className="ccd-empty">Değişen fiyat yok — dosyadaki fiyatlar paneldekiyle aynı.</div>
              ) : (
                <>
                  <div className="ccd-fieldset-title">
                    {onizleme.degisen.length} ürünün fiyatı değişecek
                  </div>
                  <div className="ccd-table-wrap">
                    <table className="ccd-table">
                      <thead>
                        <tr>
                          <th className="ccd-th">Ürün</th>
                          <th className="ccd-th ccd-td-num">Eski</th>
                          <th className="ccd-th ccd-td-num">Yeni</th>
                          <th className="ccd-th ccd-td-num">Değişim</th>
                        </tr>
                      </thead>
                      <tbody>
                        {onizleme.degisen.slice(0, 200).map((d) => (
                          <tr key={d.code} className="ccd-tr">
                            <td className="ccd-td">
                              <b className="ccd-mono">{d.code}</b>
                              <div className="ccd-meta">{d.name}</div>
                            </td>
                            <td className="ccd-td ccd-td-num">
                              {d.eskiKurus == null ? <span className="ccd-muted">yok</span> : formatTl(d.eskiKurus)}
                            </td>
                            <td className="ccd-td ccd-td-num"><b>{formatTl(d.yeniKurus)}</b></td>
                            <td className="ccd-td ccd-td-num">
                              {d.artisYuzde == null ? <span className="ccd-muted">—</span>
                                : <span className={d.artisYuzde > 0 ? 'ccd-kpi-down' : 'ccd-kpi-up'}>
                                    {d.artisYuzde > 0 ? '+' : ''}{d.artisYuzde}%
                                  </span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {onizleme.bulunamayanAdet > 0 && (
                <div className="ccd-banner ccd-banner-warning">
                  <span className="ccd-banner-icon" aria-hidden="true">?</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {onizleme.bulunamayanAdet} kod panelde yok — atlanacak
                    </div>
                    <div className="ccd-meta ccd-mono">
                      {onizleme.bulunamayan.slice(0, 12).join(', ')}
                      {onizleme.bulunamayanAdet > 12 && ' …'}
                    </div>
                  </div>
                </div>
              )}

              {onizleme.okunamayanAdet > 0 && (
                <div className="ccd-meta">
                  {onizleme.okunamayanAdet} satırın fiyatı okunamadı (boş ya da sıfır), atlandı.
                </div>
              )}

              <button className="ccd-btn ccd-btn-primary ccd-btn-lg"
                      disabled={bekliyor || onizleme.degisen.length === 0}
                      onClick={() => gonder(true)}>
                {bekliyor ? 'Yazılıyor…' : `${onizleme.degisen.length} fiyatı uygula`}
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
